import type { TeacherClass } from 'src/class/class-provider';

import { useState } from 'react';
import { Label } from '@classroom-ai/ui/components/label';
import { Iconify } from '@classroom-ai/ui/components/iconify';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import ListSubheader from '@mui/material/ListSubheader';

import { useActiveClass } from 'src/class/class-provider';

import { groupByTeacher } from 'src/sections/department/department-model';
import { useDepartmentOverview } from 'src/sections/department/use-department-overview';

import { SchoolYearMenu } from './school-year-menu';
import { useTodayPeriods } from './use-today-periods';
import {
  classLabel,
  classTitle,
  periodTime,
  periodBadge,
  contextChip,
  titleSubtitle,
  groupByCourse,
  schoolYearLabel,
  type PeriodState,
} from './header-model';

// ----------------------------------------------------------------------

/**
 * The teacher header's left side (epic #617): the class you're teaching reads as the page title,
 * with its menu grouped by course. The school and year live in the menu footer; a chip appears
 * next to the title only when they matter (more than one school, or not the current year).
 * While a lead views a colleague's class (epic #566) the title turns info-blue and its menu
 * switches between that course's sections.
 */
export function HeaderClassTitle() {
  const { activeClass, viewing, isLoading } = useActiveClass();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Skeleton variant="rounded" width={40} height={40} />
        <Skeleton variant="text" width={160} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      {viewing && activeClass ? (
        <ViewingTitle activeClass={activeClass} teacherName={viewing.teacherName} />
      ) : (
        <OwnTitle />
      )}
      <ContextChip />
    </Box>
  );
}

// ----------------------------------------------------------------------

const BADGE = {
  width: { xs: 34, sm: 40 },
  height: { xs: 34, sm: 40 },
  flexShrink: 0,
  borderRadius: 1.5,
  display: 'grid',
  placeItems: 'center',
  typography: 'subtitle2',
  fontWeight: 700,
} as const;

function TitleText({ title, short, subtitle, color, live }: { title: string; short: string; subtitle: string; color?: string; live?: boolean }) {
  return (
    <Box sx={{ minWidth: 0, textAlign: 'left' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color }}>
        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.3, display: { xs: 'none', sm: 'block' } }}>
          {title}
        </Typography>
        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, lineHeight: 1.3, display: { xs: 'block', sm: 'none' } }}>
          {short}
        </Typography>
        <Iconify icon="eva:arrow-ios-downward-fill" width={16} sx={{ flexShrink: 0, opacity: 0.7 }} />
      </Box>
      <Typography
        variant="caption"
        noWrap
        component="div"
        sx={{ color: color ?? 'text.secondary', lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: 0.75 }}
      >
        {live && <LiveDot />}
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {subtitle}
        </Box>
      </Typography>
    </Box>
  );
}

/** The green "in session" dot. */
function LiveDot() {
  return (
    <Box
      component="span"
      sx={(theme) => ({
        width: 8,
        height: 8,
        flexShrink: 0,
        borderRadius: '50%',
        bgcolor: 'success.main',
        boxShadow: `0 0 0 3px ${theme.vars.palette.success.lighter}`,
        ...theme.applyStyles('dark', { boxShadow: `0 0 0 3px ${theme.vars.palette.success.darker}` }),
      })}
    />
  );
}

const TRIGGER_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  minWidth: 0,
  py: 0.5,
  pl: 0.5,
  pr: 1,
  borderRadius: 1.5,
  '&:hover': { bgcolor: 'action.hover' },
} as const;

function OwnTitle() {
  const { classes, activeClass, setActiveClassId, schoolYearOptions, activeSchoolYearId } = useActiveClass();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [yearAnchor, setYearAnchor] = useState<HTMLElement | null>(null);

  const { states, practicing } = useTodayPeriods(classes);

  const activeYear = schoolYearOptions.find((o) => o.yearId === activeSchoolYearId) ?? null;
  const groups = groupByCourse(classes);
  const activeState = activeClass ? states.get(activeClass.id) : undefined;

  const subtitle = activeClass
    ? titleSubtitle(activeClass, activeState, practicing.get(activeClass.id) ?? 0) || (activeYear?.yearName ?? '')
    : schoolYearLabel(activeYear);

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-label={activeClass ? `${classLabel(activeClass)} — switch class` : 'No classes — choose a school year'}
        sx={[TRIGGER_SX, !!anchor && { bgcolor: 'action.hover' }]}
      >
        <Box
          sx={{
            ...BADGE,
            color: activeClass ? 'common.white' : 'text.secondary',
            bgcolor: activeClass ? 'primary.main' : 'action.selected',
          }}
        >
          {activeClass ? periodBadge(activeClass) : <Iconify icon="solar:notebook-bold-duotone" width={20} />}
        </Box>
        <TitleText
          title={activeClass ? classTitle(activeClass) : 'No classes this year'}
          short={activeClass ? activeClass.course.name : 'No classes'}
          subtitle={subtitle}
          live={activeState?.state === 'now'}
        />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 340, maxWidth: 'calc(100vw - 24px)' } } }}
      >
        {groups.flatMap((g) => [
          <ListSubheader key={`h-${g.courseId}`} sx={{ typography: 'overline', color: 'text.disabled', lineHeight: '32px' }}>
            {g.courseName}
          </ListSubheader>,
          ...g.classes.map((c) => <ClassItem key={c.id} c={c} state={states.get(c.id)} practicing={practicing.get(c.id) ?? 0} selected={c.id === activeClass?.id} onPick={() => { setAnchor(null); if (c.id !== activeClass?.id) setActiveClassId(c.id); }} />),
        ])}
        {groups.length === 0 && (
          <Typography variant="body2" sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>
            You have no classes in this school year.
          </Typography>
        )}
        <Divider sx={{ borderStyle: 'dashed' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pt: 1, pb: 0.5, color: 'text.secondary' }}>
          <Iconify icon="solar:home-angle-bold-duotone" width={18} sx={{ flexShrink: 0 }} />
          <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {schoolYearLabel(activeYear)}
          </Typography>
          {schoolYearOptions.length > 1 && (
            <Button size="small" color="primary" onClick={(e) => setYearAnchor(e.currentTarget)}>
              {new Set(schoolYearOptions.map((o) => o.schoolId)).size > 1 ? 'Change' : 'Change year'}
            </Button>
          )}
        </Box>
      </Menu>

      <SchoolYearMenu
        anchorEl={yearAnchor}
        onClose={() => {
          setYearAnchor(null);
          setAnchor(null);
        }}
      />
    </>
  );
}

type ClassItemProps = { c: TeacherClass; state: PeriodState | undefined; practicing: number; selected: boolean; onPick: () => void };

function ClassItem({ c, state, practicing, selected, onPick }: ClassItemProps) {
  const period = c.schedulePeriods?.find((sp) => sp.dailySchedule.isDefault)?.schedulePeriod?.name;
  return (
    <MenuItem selected={selected} onClick={onPick} sx={{ gap: 1.25, py: 0.75 }}>
      <Box
        sx={{
          ...BADGE,
          width: 30,
          height: 30,
          borderRadius: 1,
          typography: 'caption',
          fontWeight: 700,
          color: selected ? 'common.white' : 'text.secondary',
          bgcolor: selected ? 'primary.main' : 'action.selected',
        }}
      >
        {periodBadge(c)}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {period ?? c.academicPeriod?.name ?? c.course.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {[state?.time ?? periodTime(c), c.academicPeriod?.name].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      {state?.state === 'now' ? (
        <Label variant="soft" color="success">
          {practicing > 0 ? `Now · ${practicing} practicing` : 'Now'}
        </Label>
      ) : state?.state === 'next' ? (
        <Label variant="soft">Next</Label>
      ) : practicing > 0 ? (
        <Label variant="soft" color="success">
          {practicing} practicing
        </Label>
      ) : null}
    </MenuItem>
  );
}

// ----------------------------------------------------------------------

/** The school / year chip — only when it matters (`contextChip`). Opens the year menu. */
function ContextChip() {
  const { schoolYearOptions, activeSchoolYearId, viewing } = useActiveClass();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = schoolYearOptions.find((o) => o.yearId === activeSchoolYearId) ?? null;
  const chip = viewing ? null : contextChip(active, schoolYearOptions);
  if (!chip) return null;

  const warning = chip.tone === 'warning';
  return (
    <>
      <Tooltip title={chip.tooltip}>
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={`${chip.tooltip} Change school year`}
          sx={{
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            minWidth: 0,
            flexShrink: 0,
            display: { xs: 'none', sm: 'flex' },
            typography: 'caption',
            fontWeight: 700,
            color: warning ? 'warning.dark' : 'text.secondary',
            bgcolor: warning ? 'warning.lighter' : 'action.hover',
          }}
        >
          <Iconify icon={warning ? 'solar:clock-circle-bold' : 'solar:home-angle-bold-duotone'} width={16} sx={{ flexShrink: 0 }} />
          <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {chip.label}
          </Box>
        </ButtonBase>
      </Tooltip>
      <SchoolYearMenu anchorEl={anchor} onClose={() => setAnchor(null)} />
    </>
  );
}

// ----------------------------------------------------------------------

const norm = (id: string) => id.replace(/-/g, '').toLowerCase();

/**
 * The title while a lead views a colleague's class (epic #566). With more than one section of
 * the course in reach (`department.overview`) it's a dropdown: another teacher's section stays
 * view-only, one of yours leaves view mode.
 */
function ViewingTitle({ activeClass, teacherName }: { activeClass: TeacherClass; teacherName: string }) {
  const { setActiveClassId, startViewing } = useActiveClass();
  const { data } = useDepartmentOverview();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const course = data?.courses.find((c) => norm(c.courseId) === norm(activeClass.course.id)) ?? null;
  const canSwitch = !!course && course.sections.length > 1;

  const pick = (classId: string, mine: boolean) => {
    setAnchor(null);
    if (norm(classId) === norm(activeClass.id)) return;
    if (mine) setActiveClassId(classId);
    else startViewing(classId);
  };

  return (
    <>
      <ButtonBase
        disabled={!canSwitch}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup={canSwitch ? 'menu' : undefined}
        aria-label={`Viewing ${classLabel(activeClass)}, ${teacherName}'s class${canSwitch ? ' — switch section' : ''}`}
        sx={[TRIGGER_SX, (theme) => ({ color: 'info.dark', ...theme.applyStyles('dark', { color: 'info.light' }) })]}
      >
        <Box sx={{ ...BADGE, color: 'common.white', bgcolor: 'info.main' }}>
          <Iconify icon="solar:eye-bold" width={20} />
        </Box>
        <TitleText
          title={`${classTitle(activeClass)}`}
          short={activeClass.course.name}
          subtitle={`${teacherName}'s class · view only`}
          color="inherit"
        />
      </ButtonBase>

      {course && (
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { minWidth: 260 } } }}>
          {groupByTeacher(course).flatMap((g) => [
            <ListSubheader key={`h-${g.teacherName}-${g.mine}`}>{g.mine ? `${g.teacherName} (you)` : g.teacherName}</ListSubheader>,
            ...g.sections.map((sec) => (
              <MenuItem key={sec.classId} selected={norm(sec.classId) === norm(activeClass.id)} onClick={() => pick(sec.classId, sec.mine)}>
                {sec.label}
                {!sec.mine && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 'auto', pl: 2 }}>
                    view only
                  </Typography>
                )}
              </MenuItem>
            )),
          ])}
        </Menu>
      )}
    </>
  );
}
